import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import React, { useCallback, useEffect, useState } from 'react';

// ── Blob-based image cache ──
// Fetches images as blobs and stores them as blob: URLs so the browser never
// refetches from the CDN. Solves CDN cookie expiry and cache-control issues.

const blobCache = new Map<string, string>(); // original URL → blob: URL
const pendingFetches = new Set<string>();
const failedFetches = new Map<string, number>(); // url → retry count
const loadedImageUrls = new Set<string>();
const failedImageUrls = new Set<string>();
const imageCacheListeners = new Map<string, Set<() => void>>();
const MAX_RETRIES = 2;

export function normalizeCdnImageUrl(url: string): string {
  if (!url) return url;

  try {
    return new URL(url).toString();
  } catch {
    return url;
  }
}

function notifyImageCacheListeners(url: string) {
  const listeners = imageCacheListeners.get(url);
  if (!listeners) return;
  listeners.forEach((listener) => listener());
}

function fetchAsBlob(url: string, forceRetry = false) {
  const normalizedUrl = normalizeCdnImageUrl(url);
  if (blobCache.has(normalizedUrl) || pendingFetches.has(normalizedUrl)) return;
  if (forceRetry) {
    failedFetches.set(normalizedUrl, 0);
  }
  const retries = failedFetches.get(normalizedUrl) ?? 0;
  if (retries > MAX_RETRIES) return;
  pendingFetches.add(normalizedUrl);
  fetch(normalizedUrl, { credentials: 'include' })
    .then((r) => (r.ok ? r.blob() : null))
    .then((blob) => {
      if (blob) {
        blobCache.set(normalizedUrl, URL.createObjectURL(blob));
        failedFetches.delete(normalizedUrl);
        failedImageUrls.delete(normalizedUrl);
      } else {
        failedFetches.set(normalizedUrl, retries + 1);
      }
    })
    .catch(() => {
      failedFetches.set(normalizedUrl, retries + 1);
    })
    .finally(() => {
      pendingFetches.delete(normalizedUrl);
      notifyImageCacheListeners(normalizedUrl);
    });
}

export type CachedImageStatus = 'loaded' | 'pending' | 'failed' | 'idle';

export interface ImageCacheSummary {
  total: number;
  loaded: number;
  pending: number;
  failed: number;
  idle: number;
}

export interface PreloadImagesOptions {
  forceRetry?: boolean;
  batchSize?: number;
  delayMs?: number;
}

/** Retry fetching a failed image URL. Returns true if a retry was triggered. */
export function retryImageFetch(url: string, forceRetry = false): boolean {
  const normalizedUrl = normalizeCdnImageUrl(url);
  if (blobCache.has(normalizedUrl) || pendingFetches.has(normalizedUrl)) return false;
  const retries = failedFetches.get(normalizedUrl) ?? 0;
  if (forceRetry || retries > MAX_RETRIES) {
    // Reset counter to allow manual retry
    failedFetches.set(normalizedUrl, 0);
  }
  fetchAsBlob(normalizedUrl, forceRetry);
  return true;
}

export function ensureImageCached(url: string, forceRetry = false): boolean {
  const normalizedUrl = normalizeCdnImageUrl(url);
  if (blobCache.has(normalizedUrl) || pendingFetches.has(normalizedUrl)) return false;
  fetchAsBlob(normalizedUrl, forceRetry);
  return true;
}

export function getCachedBlobUrl(url: string): string | undefined {
  return blobCache.get(normalizeCdnImageUrl(url));
}

export function subscribeToImageCache(url: string, listener: () => void): () => void {
  const normalizedUrl = normalizeCdnImageUrl(url);
  const listeners = imageCacheListeners.get(normalizedUrl) ?? new Set<() => void>();
  listeners.add(listener);
  imageCacheListeners.set(normalizedUrl, listeners);

  return () => {
    const currentListeners = imageCacheListeners.get(normalizedUrl);
    if (!currentListeners) return;
    currentListeners.delete(listener);
    if (currentListeners.size === 0) {
      imageCacheListeners.delete(normalizedUrl);
    }
  };
}

/** Returns blob: URL if cached, otherwise original URL (and starts background fetch). */
export function getCachedImageUrl(url: string): string {
  const normalizedUrl = normalizeCdnImageUrl(url);
  return blobCache.get(normalizedUrl) ?? (ensureImageCached(normalizedUrl), normalizedUrl);
}

/** Returns true if the URL has failed and exhausted retries. */
export function isImageFailed(url: string): boolean {
  const normalizedUrl = normalizeCdnImageUrl(url);
  return (failedFetches.get(normalizedUrl) ?? 0) > MAX_RETRIES && !blobCache.has(normalizedUrl);
}

export function markImageLoaded(url: string) {
  const normalizedUrl = normalizeCdnImageUrl(url);
  loadedImageUrls.add(normalizedUrl);
  failedImageUrls.delete(normalizedUrl);
  notifyImageCacheListeners(normalizedUrl);
}

export function markImageFailed(url: string) {
  const normalizedUrl = normalizeCdnImageUrl(url);
  if (!loadedImageUrls.has(normalizedUrl)) {
    failedImageUrls.add(normalizedUrl);
  }
  notifyImageCacheListeners(normalizedUrl);
}

export function clearImageFailure(url: string) {
  const normalizedUrl = normalizeCdnImageUrl(url);
  failedImageUrls.delete(normalizedUrl);
  notifyImageCacheListeners(normalizedUrl);
}

export function getCachedImageStatus(url: string): CachedImageStatus {
  const normalizedUrl = normalizeCdnImageUrl(url);
  if (loadedImageUrls.has(normalizedUrl) || blobCache.has(normalizedUrl)) return 'loaded';
  if (pendingFetches.has(normalizedUrl)) return 'pending';
  if (failedImageUrls.has(normalizedUrl) || isImageFailed(normalizedUrl)) return 'failed';
  return 'idle';
}

export function getImageCacheSummary(urls: string[]): ImageCacheSummary {
  const uniqueUrls = [...new Set(urls.map((url) => normalizeCdnImageUrl(url)))];
  let loaded = 0;
  let pending = 0;
  let failed = 0;
  let idle = 0;

  for (const url of uniqueUrls) {
    const status = getCachedImageStatus(url);
    if (status === 'loaded') loaded++;
    else if (status === 'pending') pending++;
    else if (status === 'failed') failed++;
    else idle++;
  }

  return {
    total: uniqueUrls.length,
    loaded,
    pending,
    failed,
    idle,
  };
}

/** Revoke blob URLs for a set of original URLs (call when removing a UPC). */
export function releaseCachedImages(urls: string[]) {
  for (const originalUrl of urls) {
    const url = normalizeCdnImageUrl(originalUrl);
    const blobUrl = blobCache.get(url);
    if (blobUrl) {
      URL.revokeObjectURL(blobUrl);
      blobCache.delete(url);
    }
    loadedImageUrls.delete(url);
    failedImageUrls.delete(url);
    pendingFetches.delete(url);
    failedFetches.delete(url);
    imageCacheListeners.delete(url);
  }
}

// ── Zoom preview hook ──

const ZOOM_STORAGE_KEY = 'forge-zoom-enabled';

export interface ZoomMeta {
  metadataType?: 'design';
  id?: string;
  storeId?: string;
  source?: string;
  forgeOriginSource?: string;
  barcode?: string;
  normalizedBarcode?: string;
  normalizedUpc?: string;
  classificationType?: string;
  warningMessage?: string;
  barcodeStatus?: string;
  cropCount?: number;
  noteCount?: number;
  imageResolution?: string;
  createdBy?: string;
  createdAt?: string;
  updatedBy?: string;
  updatedAt?: string;
}

interface ZoomState {
  url?: string;
  x: number;
  y: number;
  meta?: ZoomMeta;
}

export function useZoomPreview() {
  const [zoomImage, setZoomImage] = useState<ZoomState | null>(null);
  const [zoomEnabled, setZoomEnabled] = useState<boolean>(() => {
    try {
      return localStorage.getItem(ZOOM_STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const clearZoomImage = useCallback(() => setZoomImage(null), []);

  useEffect(() => {
    try {
      localStorage.setItem(ZOOM_STORAGE_KEY, String(zoomEnabled));
    } catch {
      /* ignore */
    }
  }, [zoomEnabled]);

  useEffect(() => {
    if (!zoomImage) return;

    const handleWindowBlur = () => {
      clearZoomImage();
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        clearZoomImage();
      }
    };

    const handleViewportMouseOut = (event: MouseEvent) => {
      if (event.relatedTarget === null) {
        clearZoomImage();
      }
    };

    const handleViewportExit = () => {
      clearZoomImage();
    };

    window.addEventListener('blur', handleWindowBlur);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('mouseout', handleViewportMouseOut);
    window.addEventListener('scroll', handleViewportExit, true);
    window.addEventListener('wheel', handleViewportExit, { passive: true });

    return () => {
      window.removeEventListener('blur', handleWindowBlur);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('mouseout', handleViewportMouseOut);
      window.removeEventListener('scroll', handleViewportExit, true);
      window.removeEventListener('wheel', handleViewportExit);
    };
  }, [clearZoomImage, zoomImage]);

  const toggleZoom = useCallback(() => {
    setZoomEnabled((prev) => {
      if (prev) {
        clearZoomImage();
      }
      return !prev;
    });
  }, [clearZoomImage]);

  const preloadImages = useCallback((urls: string[], options?: PreloadImagesOptions) => {
    const forceRetry = options?.forceRetry ?? false;
    const batchSize = options?.batchSize ?? 4;
    const delayMs = options?.delayMs ?? 100;
    const fresh = urls.filter((u) => !blobCache.has(u) && !pendingFetches.has(u));
    if (fresh.length === 0) return;
    let i = 0;
    const next = () => {
      fresh.slice(i, i + batchSize).forEach((url) => {
        if (forceRetry) retryImageFetch(url, true);
        else fetchAsBlob(url);
      });
      i += batchSize;
      if (i < fresh.length) setTimeout(next, delayMs);
    };
    next();
  }, []);

  const shouldZoom = useCallback((e: React.MouseEvent) => zoomEnabled || e.altKey, [zoomEnabled]);

  const handleImageHover = useCallback(
    (e: React.MouseEvent, url: string, meta?: ZoomMeta) => {
      fetchAsBlob(url);
      if (shouldZoom(e)) setZoomImage({ url, x: e.clientX, y: e.clientY, meta });
    },
    [shouldZoom]
  );

  const handleImageMove = useCallback(
    (e: React.MouseEvent, url: string, meta?: ZoomMeta) => {
      if (shouldZoom(e)) setZoomImage({ url, x: e.clientX, y: e.clientY, meta });
      else setZoomImage(null);
    },
    [shouldZoom]
  );

  const handleImageLeave = useCallback(() => setZoomImage(null), []);

  const handleMetadataHover = useCallback(
    (e: React.MouseEvent, meta?: ZoomMeta) => {
      if (meta && shouldZoom(e)) setZoomImage({ x: e.clientX, y: e.clientY, meta });
    },
    [shouldZoom]
  );

  const handleMetadataMove = useCallback(
    (e: React.MouseEvent, meta?: ZoomMeta) => {
      if (meta && shouldZoom(e)) setZoomImage({ x: e.clientX, y: e.clientY, meta });
      else setZoomImage(null);
    },
    [shouldZoom]
  );

  const handleMetadataLeave = useCallback(() => setZoomImage(null), []);

  const meta = zoomImage?.meta;
  const hasMeta = !!meta?.id;
  const hasImage = Boolean(zoomImage?.url);

  const formatDate = (iso: string) => {
    try {
      const d = new Date(iso);
      return (
        d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) +
        ' ' +
        d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
      );
    } catch {
      return iso;
    }
  };

  const zoomW = 312;
  const zoomImgH = 312;
  const metaOnlyEstimatedH = 190;
  const truncate = (s: string, max: number) => (s.length > max ? s.slice(0, max - 1) + '…' : s);

  const scanBadge = (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 10,
        height: 10,
        padding: '0 2px',
        borderRadius: 999,
        background: 'rgba(63, 185, 80, 0.92)',
        border: '1px solid rgba(164, 239, 176, 0.55)',
        color: '#f3fff5',
        fontFamily: 'var(--font-mono)',
        fontSize: 7,
        fontWeight: 700,
        lineHeight: 1,
        marginRight: 4,
        verticalAlign: 'middle',
      }}
    >
      S
    </span>
  );

  const forgeBadge = (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 10,
        height: 10,
        padding: '0 2px',
        borderRadius: 999,
        background: 'rgba(139, 92, 246, 0.92)',
        border: '1px solid rgba(196, 181, 253, 0.6)',
        color: '#faf5ff',
        fontFamily: 'var(--font-mono)',
        fontSize: 7,
        fontWeight: 700,
        lineHeight: 1,
        marginRight: 4,
        verticalAlign: 'middle',
      }}
    >
      F
    </span>
  );

  const renderSourceValue = (source: string) => {
    if (source === 'scan') {
      return (
        <>
          <span>{scanBadge}</span>scan
        </>
      );
    }
    if (source === 'forge') {
      return (
        <>
          <span>{forgeBadge}</span>
          forge
        </>
      );
    }
    return source;
  };

  const metaRow = (label: string, value: React.ReactNode) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, minWidth: 0 }}>
      <span style={{ color: 'var(--text-tertiary)', flexShrink: 0 }}>{label}</span>
      <span
        style={{
          color: 'var(--text-primary)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {value}
      </span>
    </div>
  );

  const ZoomOverlay = zoomImage ? (
    <div
      style={{
        position: 'fixed',
        left: Math.min(zoomImage.x + 16, window.innerWidth - zoomW - 16),
        top: Math.max(
          Math.min(
            hasImage ? zoomImage.y - zoomImgH / 2 : zoomImage.y + 16,
            window.innerHeight -
              (hasImage ? (hasMeta ? zoomImgH + 180 : zoomImgH + 16) : metaOnlyEstimatedH)
          ),
          8
        ),
        width: zoomW,
        borderRadius: 'var(--radius)',
        border: '2px solid var(--border)',
        background: 'var(--surface)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
        overflow: 'hidden',
        pointerEvents: 'none',
        zIndex: 10000,
      }}
    >
      {hasImage && zoomImage.url ? (
        <div style={{ width: zoomW, height: zoomImgH, overflow: 'hidden' }}>
          <img
            src={getCachedImageUrl(zoomImage.url)}
            alt="Zoom"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              background: '#000',
              display: 'block',
            }}
          />
        </div>
      ) : null}
      {hasMeta && (
        <div
          style={{
            padding: '6px 8px',
            borderTop: hasImage ? '1px solid var(--border)' : 'none',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            fontFamily: 'var(--font-mono)',
            fontSize: 9,
            lineHeight: 1.4,
            overflow: 'hidden',
          }}
        >
          {meta!.warningMessage && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '2px 0 4px',
                color: 'var(--warning)',
                borderBottom: '1px solid rgba(227,179,65,0.16)',
                marginBottom: 2,
              }}
            >
              <WarningAmberOutlinedIcon style={{ fontSize: 12, flexShrink: 0 }} />
              <span
                style={{
                  color: 'var(--warning)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {meta!.warningMessage}
              </span>
            </div>
          )}
          {meta!.metadataType === 'design' ? (
            <>
              {metaRow('design_id', meta!.id || '-')}
              {metaRow('barcode', meta!.normalizedBarcode || meta!.barcode || '-')}
              {metaRow('status', meta!.barcodeStatus || '-')}
              {metaRow('created', meta!.createdAt ? formatDate(meta!.createdAt) : '-')}
              {metaRow('updated', meta!.updatedAt ? formatDate(meta!.updatedAt) : '-')}
              {metaRow('updated_by', meta!.updatedBy ? truncate(meta!.updatedBy, 28) : '-')}
              {metaRow('crops', meta!.cropCount ?? '-')}
              {metaRow('notes', meta!.noteCount ?? '-')}
            </>
          ) : (
            <>
              {metaRow('id', meta!.id!)}
              {meta!.storeId && metaRow('store_id', meta!.storeId)}
              {meta!.source && metaRow('source', renderSourceValue(meta!.source))}
              {meta!.forgeOriginSource && metaRow('forge_origin_source', meta!.forgeOriginSource)}
              {meta!.classificationType && metaRow('classification_type', meta!.classificationType)}
              {meta!.barcode && metaRow('barcode', meta!.barcode)}
              {meta!.normalizedBarcode && metaRow('normalized_barcode', meta!.normalizedBarcode)}
              {meta!.normalizedUpc && metaRow('normalized_upc', meta!.normalizedUpc)}
              {meta!.barcodeStatus && metaRow('status', meta!.barcodeStatus)}
              {meta!.cropCount !== undefined && metaRow('crops', meta!.cropCount)}
              {meta!.noteCount !== undefined && metaRow('notes', meta!.noteCount)}
              {meta!.imageResolution && metaRow('resolution', meta!.imageResolution)}
              {meta!.createdBy && metaRow('created_by', truncate(meta!.createdBy, 28))}
              {meta!.createdAt && metaRow('created_at', formatDate(meta!.createdAt))}
              {meta!.updatedBy && metaRow('updated_by', truncate(meta!.updatedBy, 28))}
              {meta!.updatedAt && metaRow('updated_at', formatDate(meta!.updatedAt))}
            </>
          )}
        </div>
      )}
    </div>
  ) : null;

  return {
    handleImageHover,
    handleImageMove,
    handleImageLeave,
    handleMetadataHover,
    handleMetadataMove,
    handleMetadataLeave,
    ZoomOverlay,
    preloadImages,
    zoomEnabled,
    toggleZoom,
  };
}
